/**
 * Calls placed inside the app — Twilio Voice SDK (WebRTC). The customer sees (249) 700-4555 and nothing
 * rings anywhere else: the phone or computer running the app is the phone (mic + speaker).
 *
 *   1. n8n "[C] App 9 - Call Customer" (REACT_APP_CALL_URL) gives a signed-in staff member a 1-hour
 *      calling pass (Twilio access token, outgoing calls only).
 *   2. device.connect → Twilio asks n8n "[C] Twilio 3 - Calls" (/voice-app) → <Dial> the customer.
 *   3. When it ends, the same workflow logs it in Communications + a Note in Zoho.
 * Calls from your own phone's dialer stay normal calls from that phone.
 */
import { useSyncExternalStore } from 'react';
import { AUTH_PROVIDER, getAccessToken } from './auth';
import { toE164 } from './communications';

const CALL_URL = process.env.REACT_APP_CALL_URL;
export const APP_CALLS_LIVE = !!CALL_URL && AUTH_PROVIDER === 'supabase';

/* ---------- the call bar's state ---------- */
// status: 'idle' | 'connecting' | 'ringing' | 'in-call' | 'ended' | 'error'
let state = { status: 'idle', name: '', phone: '', startedAt: null, muted: false, error: '' };
const listeners = new Set();
const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
};
export const useAppCall = () =>
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state
  );

/* ---------- calling pass + device ---------- */
let pass = null; // { token, by, expiresAt }
const getPass = async () => {
  if (pass && pass.expiresAt - Date.now() > 120000) return pass;
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to call');
  let res;
  try {
    res = await fetch(CALL_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}' });
  } catch {
    throw new Error('No connection — try again');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `Couldn’t start calling (${res.status})`);
  pass = out;
  return pass;
};

let device = null;
let current = null; // the active Call
const getDevice = async () => {
  const p = await getPass();
  const { Device } = await import('@twilio/voice-sdk'); // only loaded when someone actually calls
  if (!device) {
    device = new Device(p.token, { codecPreferences: ['opus', 'pcmu'], closeProtection: 'A call is still going — leave anyway?' });
    device.on('tokenWillExpire', async () => {
      pass = null;
      try {
        device.updateToken((await getPass()).token);
      } catch {
        /* next call fetches a fresh pass */
      }
    });
  } else {
    device.updateToken(p.token);
  }
  return { device, by: p.by };
};

const finish = (patch = {}) => {
  current = null;
  set({ status: 'ended', ...patch });
  setTimeout(() => state.status === 'ended' && set({ status: 'idle' }), 2500);
};

/** Call a customer from the app. name is only for the call bar. */
export const callCustomer = async (customerPhone, name = '') => {
  const to = toE164(customerPhone);
  if (!to) throw new Error('No valid phone number for this customer');
  if (current) throw new Error('You’re already on a call');
  set({ status: 'connecting', name, phone: to, startedAt: null, muted: false, error: '' });
  try {
    const { device: d, by } = await getDevice();
    const call = await d.connect({ params: { To: to, By: by } });
    current = call;
    call.on('ringing', () => set({ status: 'ringing' }));
    call.on('accept', () => set({ status: 'in-call', startedAt: Date.now() }));
    call.on('disconnect', () => finish());
    call.on('cancel', () => finish());
    call.on('reject', () => finish());
    call.on('error', (err) => finish({ status: 'error', error: (err && err.message) || 'The call dropped' }));
    call.on('mute', (muted) => set({ muted }));
    return call;
  } catch (err) {
    const msg = /permission|NotAllowed/i.test(err.message || '') ? 'Allow the microphone to make calls from the app' : err.message;
    finish({ status: 'error', error: msg });
    throw new Error(msg);
  }
};

export const hangUp = () => {
  if (current) current.disconnect();
  else if (device) device.disconnectAll();
};
export const toggleMute = () => {
  if (current) current.mute(!current.isMuted());
};
